// Keyframed animation for project-registered character rigs.
//
// The engine already ships clip playback for the built-in prototype biped, but
// that is a baked glTF: it only plays what was exported with the model. A game
// that hand-builds a rig (as The Dwindling does for Being) has no glTF to
// bake into, and its animation is the character. This gives those rigs authored
// clips in the same shape the pose system already uses, so one `CharacterPose`
// is simply a clip of length zero and everything downstream is unchanged.
//
// Deliberately small: keyframes of poses, eased between. No IK, no blend trees,
// no additive layers. Those are real features and they are not what is missing.

import type { CharacterPose, RigBoneTransform } from '../../content/schema/character';

/** How a keyframe approaches the next one. */
export type ClipEasing = 'linear' | 'ease' | 'easeIn' | 'easeOut' | 'hold';

export interface ClipKeyframe {
  /** Seconds from the start of the clip. Keyframes are sorted on validation. */
  time: number;
  /**
   * The bones this keyframe speaks about. A bone the whole clip never mentions
   * keeps whatever the character's rest attitude gives it, so a clip that only
   * moves an arm is a legitimate clip.
   */
  boneTransforms: Record<string, RigBoneTransform>;
  /** How the value approaches the *next* keyframe. Defaults to 'ease'. */
  easing?: ClipEasing;
}

export interface CharacterClip {
  id: string;
  name: string;
  rigId: string;
  /** Seconds. Derived from the last keyframe when omitted. */
  duration?: number;
  loop?: boolean;
  description?: string;
  keyframes: ClipKeyframe[];
}

/** Total length of a clip in seconds. */
export function clipDuration(clip: CharacterClip): number {
  if (clip.duration != null) return clip.duration;
  return clip.keyframes.reduce((max, k) => Math.max(max, k.time), 0);
}

function ease(t: number, kind: ClipEasing): number {
  switch (kind) {
    case 'linear':
      return t;
    case 'hold':
      return 0;
    case 'easeIn':
      return t * t;
    case 'easeOut':
      return t * (2 - t);
    case 'ease':
    default:
      // Smoothstep. Gentle at both ends, which is the register the whole
      // character is written in: he never hurries and he never snaps to.
      return t * t * (3 - 2 * t);
  }
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function blendTransform(a: RigBoneTransform, b: RigBoneTransform, t: number): RigBoneTransform {
  const out: RigBoneTransform = {};
  if (a.rotation || b.rotation) {
    out.rotation = lerp3(a.rotation ?? b.rotation ?? [0, 0, 0], b.rotation ?? a.rotation ?? [0, 0, 0], t);
  }
  if (a.position || b.position) {
    out.position = lerp3(a.position ?? b.position ?? [0, 0, 0], b.position ?? a.position ?? [0, 0, 0], t);
  }
  if (a.scale || b.scale) {
    out.scale = lerp3(a.scale ?? b.scale ?? [1, 1, 1], b.scale ?? a.scale ?? [1, 1, 1], t);
  }
  return out;
}

/**
 * A clip's keyframes are static once authored (nothing in the engine or its
 * consumers mutates `clip.keyframes` or a keyframe's `boneTransforms` in
 * place - an edit always produces a new `CharacterClip` object, the same
 * immutable-update convention the rest of the store-driven engine follows).
 * So the sort, and the per-bone "which keyframes actually mention me" split,
 * are both work that only ever needs doing once per clip, not once per
 * `samplePose` call - which is every frame, for every bone, for however many
 * characters are mid-animation at once. Keyed by object identity: an edited
 * clip is a new object, which simply gets its own fresh entry, and the old
 * one falls out of the cache on its own once nothing references it.
 */
interface ClipCache {
  duration: number;
  /** Every keyframe that mentions a given bone, sorted, time-ascending. */
  byBone: Map<string, ClipKeyframe[]>;
}

const clipCache = new WeakMap<CharacterClip, ClipCache>();

function getClipCache(clip: CharacterClip): ClipCache {
  const cached = clipCache.get(clip);
  if (cached) return cached;

  const sorted = [...clip.keyframes].sort((a, b) => a.time - b.time);
  const byBone = new Map<string, ClipKeyframe[]>();
  for (const frame of sorted) {
    for (const bone of Object.keys(frame.boneTransforms)) {
      let own = byBone.get(bone);
      if (!own) {
        own = [];
        byBone.set(bone, own);
      }
      own.push(frame);
    }
  }

  const cache: ClipCache = { duration: clipDuration(clip), byBone };
  clipCache.set(clip, cache);
  return cache;
}

/**
 * The pose a clip holds at `time`.
 *
 * Bones are resolved independently, so a bone that only appears in keyframes 0
 * and 3 interpolates smoothly across 1 and 2 rather than snapping when they
 * arrive, which is what lets a clip be authored sparsely, a few bones at a
 * time, the way somebody actually works.
 *
 * Past the end: a looping clip wraps, a one-shot holds its final frame.
 */
export function samplePose(clip: CharacterClip, time: number): CharacterPose {
  const { duration, byBone } = getClipCache(clip);
  const t = duration > 0 && clip.loop ? ((time % duration) + duration) % duration : Math.min(time, duration);

  const boneTransforms: Record<string, RigBoneTransform> = {};
  for (const [bone, own] of byBone) {
    let before = own[0]!;
    let after = own[own.length - 1]!;
    for (let i = 0; i < own.length; i += 1) {
      if (own[i]!.time <= t) before = own[i]!;
      if (own[i]!.time >= t) {
        after = own[i]!;
        break;
      }
    }

    const span = after.time - before.time;
    const raw = span <= 0 ? 0 : (t - before.time) / span;
    const blended = ease(Math.max(0, Math.min(1, raw)), before.easing ?? 'ease');
    boneTransforms[bone] = blendTransform(
      before.boneTransforms[bone]!,
      after.boneTransforms[bone]!,
      blended,
    );
  }

  return { id: `${clip.id}@${t.toFixed(3)}`, name: clip.name, rigId: clip.rigId, boneTransforms };
}

/**
 * Problems that would make a clip misbehave silently.
 *
 * Returns human-readable strings rather than throwing: a half-authored clip
 * should still be previewable, and the studio can show what is wrong beside it.
 */
export function validateClip(clip: CharacterClip, rigBones?: readonly string[]): string[] {
  const issues: string[] = [];
  if (clip.keyframes.length === 0) issues.push('has no keyframes');
  if (clip.keyframes.some((k) => k.time < 0)) issues.push('has a keyframe at a negative time');
  const times = clip.keyframes.map((k) => k.time);
  if (new Set(times).size !== times.length) issues.push('has two keyframes at the same time');
  if (clip.duration != null && clip.duration < Math.max(0, ...times)) {
    issues.push('declares a duration shorter than its last keyframe');
  }
  if (rigBones) {
    const known = new Set(rigBones);
    for (const frame of clip.keyframes) {
      for (const bone of Object.keys(frame.boneTransforms)) {
        if (!known.has(bone)) issues.push(`targets '${bone}', which is not a bone of ${clip.rigId}`);
      }
    }
  }
  return [...new Set(issues)];
}
